import React from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { formatHkt, hktDayEndUtc, hktDayStartUtc } from "@shared/lib/datetime";
import type { AuditFilters as Filters } from "@shared/api/audit";

// F-E Phase 4b — filter bar. Date pickers are HKT calendar days converted to a
// UTC range for the API (wire is UTC; presentation is HKT). Other filters map
// straight to the API's snake_case query params via the audit.ts wrapper.

interface Props {
  filters: Filters;
  onChange: (partial: Partial<Filters>) => void;
  onReset: () => void;
}

function daysAgoUtc(days: number): string {
  return new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
}

// UTC ISO → HKT "YYYY-MM-DD" for a <input type="date"> value.
function isoToHktDay(iso: string | undefined): string {
  return iso ? formatHkt(iso).slice(0, 10) : "";
}

const fieldCls = "min-w-[8rem]";

const AuditFilters: React.FC<Props> = ({ filters, onChange, onReset }) => {
  // Presets are open-ended (2026-07-24): setting `to` would freeze the
  // range and hide events that happen after the click.
  const setPreset = (days: number) =>
    onChange({ from: daysAgoUtc(days), to: undefined });

  return (
    <div className="bg-white border border-border rounded-lg p-3 flex flex-wrap items-end gap-3">
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(1)}
          data-testid="audit-preset-24h"
        >
          24h
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(7)}
          data-testid="audit-preset-7d"
        >
          7d
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreset(30)}
          data-testid="audit-preset-30d"
        >
          30d
        </Button>
      </div>

      <label className="text-xs text-muted-foreground">
        From (HKT)
        <Input
          type="date"
          className={fieldCls}
          value={isoToHktDay(filters.from)}
          onChange={(e) =>
            onChange({
              from: e.target.value ? hktDayStartUtc(e.target.value) : undefined,
            })
          }
          data-testid="audit-filter-from"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        To (HKT)
        <Input
          type="date"
          className={fieldCls}
          value={isoToHktDay(filters.to)}
          onChange={(e) =>
            onChange({
              to: e.target.value ? hktDayEndUtc(e.target.value) : undefined,
            })
          }
          data-testid="audit-filter-to"
        />
      </label>

      <label className="text-xs text-muted-foreground">
        Actor
        <Input
          className={fieldCls}
          placeholder="username or usr_…"
          value={filters.actorId ?? ""}
          onChange={(e) => onChange({ actorId: e.target.value || undefined })}
          data-testid="audit-filter-actor"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Action
        <Input
          className={fieldCls}
          placeholder="e.g. create, publish, auth:login"
          value={filters.action ?? ""}
          onChange={(e) => onChange({ action: e.target.value || undefined })}
          data-testid="audit-filter-action"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Resource
        <Input
          className={fieldCls}
          placeholder="title / name or exh_…"
          value={filters.resourceId ?? ""}
          onChange={(e) =>
            onChange({ resourceId: e.target.value || undefined })
          }
          data-testid="audit-filter-resource"
        />
      </label>
      <label className="text-xs text-muted-foreground">
        Decision
        <select
          className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={filters.decision ?? ""}
          onChange={(e) =>
            onChange({
              decision: (e.target.value || undefined) as Filters["decision"],
            })
          }
          data-testid="audit-filter-decision"
        >
          <option value="">All</option>
          <option value="allow">Allow ✓</option>
          <option value="deny">Deny ✗</option>
        </select>
      </label>

      <Button
        variant="ghost"
        size="sm"
        onClick={onReset}
        data-testid="audit-filter-reset"
      >
        Reset
      </Button>
    </div>
  );
};

export default AuditFilters;
